import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useAtomValue } from 'jotai';
import {
  AlertCircleIcon,
  ChevronDownIcon,
  Camera,
  CheckIcon,
  Pencil,
  Trash2,
  XIcon,
} from 'lucide-react-native';
import { Controller } from 'react-hook-form';
import { Pressable } from 'react-native';
import { KeyboardAwareScrollView } from 'react-native-keyboard-controller';
import { twJoin } from 'tailwind-merge';

import { primum_default_avatar } from '@/assets/images';
import { getCancelHeaderProps, headerItem } from '@/components/HeaderActions';
import { SubspecialtyMultiSelect } from '@/components/SubspecialtyMultiSelect';
import { Header } from '@/components/header';
import { SupportForm } from '@/components/supportForm';
import { Avatar, AvatarImage } from '@/components/ui/avatar';
import { Box } from '@/components/ui/box';
import { Button, ButtonText } from '@/components/ui/button';
import { Checkbox, CheckboxIcon, CheckboxIndicator } from '@/components/ui/checkbox';
import {
  FormControl,
  FormControlLabel,
  FormControlLabelText,
  FormControlError,
  FormControlErrorIcon,
  FormControlErrorText,
} from '@/components/ui/form-control';
import { Heading } from '@/components/ui/heading';
import { HStack } from '@/components/ui/hstack';
import { Icon } from '@/components/ui/icon';
import { Input, InputField } from '@/components/ui/input';
import { LinkText } from '@/components/ui/link';
import {
  Modal,
  ModalBackdrop,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
} from '@/components/ui/modal';
import {
  Select,
  SelectTrigger,
  SelectInput,
  SelectIcon,
  SelectContent,
  SelectItem,
  SelectPortal,
  SelectBackdrop,
  SelectDragIndicatorWrapper,
  SelectDragIndicator,
  SelectScrollView,
} from '@/components/ui/select';
import { Spinner } from '@/components/ui/spinner';
import { Text } from '@/components/ui/text';
import { VStack } from '@/components/ui/vstack';
import { useConfirmDiscard } from '@/components/useConfirmDiscard';
import { ROUTES } from '@/constants/routes';
import { US_STATES } from '@/constants/states';
import { useSupportPrompt } from '@/features/support-contact';
import { useEditProfessional } from '@/hooks/useEditProfessional';
import { useGoBack } from '@/hooks/useGoBack';
import { getAvatarUrl } from '@/util/getAvatarUrl';

type AvatarActionIcon = typeof Camera;

function avatarActionIconForProfile(hasAvatar: boolean, isWebPlatform: boolean): AvatarActionIcon {
  if (hasAvatar) {
    return Trash2;
  }

  if (isWebPlatform) {
    return Pencil;
  }

  return Camera;
}

const specialtyOptions = [
  'Medical Oncology',
  'Hematology',
  'Hematology & Oncology',
  'Radiation Oncology',
  'Surgical Oncology',
  'Gynecologic Oncology',
  'Pediatric Oncology',
  'Palliative Care',
  'Supportive Oncology',
  'Bone Marrow Transplant',
  'Stem Cell Transplant',
  'Oncology Pharmacy',
  'Oncology Nurse Practitioner',
  'Oncology Physician Assistant',
  'Pathology',
  'Radiology',
  'Interventional Radiology',
  'Dermatology',
  'Pulmonary Medicine',
  'Gastroenterology',
  'Urology',
  'Gynecology',
  'Internal Medicine',
  'Family Medicine',
  'Primary Care',
  'Other',
];

function EditProfessionalScreen() {
  const {
    form,
    isLoading,
    userData,
    subspecialtiesQuery,
    isFellow,
    setIsFellow,
    isOwnProfile,
    fileInputRef,
    handleFileChange,
    onSubmit,
    userId,
    handleAvatarClick,
    handleRemoveAvatar,
    showRemoveAvatarModal,
    setShowRemoveAvatarModal,
    handleRemoveAvatarRequest,
    isRemovingAvatar,
  } = useEditProfessional();

  const goBack = useGoBack(ROUTES.ACCOUNT_PROFILE);

  const { isDirty } = form.formState;

  const supportPrompt = useSupportPrompt({
    source: 'professional_profile',
    draft: 'I’d like to change my focus areas: ',
  });

  const avatarActionIcon = avatarActionIconForProfile(!!userData?.avatar, isWeb);

  const handleBack = () => {
    goBack();
  };

  const { confirmCancel, discardModal } = useConfirmDiscard({
    isDirty,
    onDiscard: handleBack,
  });

  if (isLoading) {
    return (
      <VStack className="flex-1 items-center justify-center">
        <Spinner size="large" />
      </VStack>
    );
  }

  const subspecialties = subspecialtiesQuery.data || [];

  return (
    <>
      <VStack className="flex-1 bg-background-0 md:mx-auto md:w-full md:max-w-screen-md">
        <Header
          contentScrollsUnderHeader
          {...getCancelHeaderProps(confirmCancel)}
          title="Edit Professional Info"
          rightItems={[
            headerItem.save({ onPress: form.handleSubmit(onSubmit), isDisabled: !isDirty }),
          ]}
        />

        {discardModal}
        <KeyboardAwareScrollView
          contentInsetAdjustmentBehavior="automatic"
          mode="layout"
          className="flex-1"
          contentContainerStyle={{ paddingBottom: 100 }}>
          <VStack className="p-5">
            <VStack className="items-center">
              <Pressable
                onPress={userData?.avatar ? handleRemoveAvatarRequest : handleAvatarClick}
                className="relative">
                <Avatar size="2xl" className="mb-6">
                  <AvatarImage
                    source={
                      getAvatarUrl(userData?.avatar, true, { large: true }) || primum_default_avatar
                    }
                  />
                </Avatar>
                <VStack className="absolute bottom-6 right-0 h-8 w-8 items-center justify-center rounded-full border-2 border-background-0 bg-primary-500">
                  <Icon as={avatarActionIcon} size="sm" className="text-white" />
                </VStack>
              </Pressable>
              {isWeb && isOwnProfile && (
                <input
                  type="file"
                  ref={fileInputRef}
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  accept="image/png, image/jpeg"
                />
              )}
            </VStack>
            <HStack space="md">
              <Controller
                control={form.control}
                name="firstName"
                render={({ field }) => (
                  <FormControl isInvalid={!!form.formState.errors.firstName} className="flex-1">
                    <FormControlLabel className="mb-1">
                      <FormControlLabelText size="sm">First Name</FormControlLabelText>
                    </FormControlLabel>
                    <Input size="sm">
                      <InputField
                        autoFocus
                        placeholder="First Name"
                        value={field.value}
                        onChangeText={field.onChange}
                        returnKeyType="next"
                      />
                    </Input>
                    <FormControlError className="mt-1">
                      <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                      <FormControlErrorText size="xs">
                        {form.formState.errors.firstName?.message as string}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>
                )}
              />
              <Controller
                control={form.control}
                name="lastName"
                render={({ field }) => (
                  <FormControl isInvalid={!!form.formState.errors.lastName} className="flex-1">
                    <FormControlLabel className="mb-1">
                      <FormControlLabelText size="sm">Last Name</FormControlLabelText>
                    </FormControlLabel>
                    <Input size="sm">
                      <InputField
                        placeholder="Last Name"
                        value={field.value}
                        onChangeText={field.onChange}
                        returnKeyType="next"
                      />
                    </Input>
                    <FormControlError className="mt-1">
                      <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                      <FormControlErrorText size="xs">
                        {form.formState.errors.lastName?.message as string}
                      </FormControlErrorText>
                    </FormControlError>
                  </FormControl>
                )}
              />
            </HStack>
            <FormControl className="mt-4">
              <HStack space="md" className="items-start">
                <FormControlLabel className="mb-1 flex-1">
                  <FormControlLabelText size="sm">Specialty</FormControlLabelText>
                </FormControlLabel>
                {userData?.userType !== 'expert' && (
                  <FormControlLabel className="mb-1 ml-4 items-center">
                    <FormControlLabelText size="sm">Fellow</FormControlLabelText>
                  </FormControlLabel>
                )}
              </HStack>
              <HStack space="md" className="items-start">
                <Box className="flex-1">
                  <Controller
                    control={form.control}
                    name="specialty"
                    render={({ field }) => (
                      <Select
                        className="w-full"
                        selectedValue={field.value.replace(/,?\s*Fellow$/, '') || ''}
                        onValueChange={field.onChange}>
                        <SelectTrigger className="justify-between">
                          <SelectInput placeholder="Select specialty" className="text-base" />
                          <SelectIcon className="mr-3" as={ChevronDownIcon} />
                        </SelectTrigger>
                        <SelectPortal>
                          <SelectBackdrop />
                          <SelectContent className="max-h-[70%]">
                            <SelectDragIndicatorWrapper>
                              <SelectDragIndicator />
                            </SelectDragIndicatorWrapper>
                            <SelectScrollView>
                              <SelectItem key="none-option" label="None" value="" />
                              {specialtyOptions.map((option) => (
                                <SelectItem key={option} label={option} value={option} />
                              ))}
                            </SelectScrollView>
                          </SelectContent>
                        </SelectPortal>
                      </Select>
                    )}
                  />
                </Box>
                {userData?.userType !== 'expert' && (
                  <VStack className="ml-4 mt-1 items-start">
                    <Checkbox
                      value="Fellow"
                      isChecked={isFellow}
                      onChange={(isChecked: boolean) => {
                        setIsFellow(isChecked);

                        const strippedSpecialty = (form.getValues('specialty') || '')
                          .replace(/,?\s*Fellow$/, '')
                          .trim();

                        form.setValue('specialty', strippedSpecialty, { shouldDirty: true });

                        form.setValue('specialty', `${strippedSpecialty} `, { shouldDirty: true });

                        form.setValue('specialty', strippedSpecialty, { shouldDirty: true });

                        form.trigger();

                        form.setValue(
                          'fellowDirtyNudge',
                          (form.getValues('fellowDirtyNudge') || 0) + 1,
                          { shouldDirty: true }
                        );
                      }}
                      size="md">
                      <CheckboxIndicator>
                        <CheckboxIcon as={CheckIcon} />
                      </CheckboxIndicator>
                    </Checkbox>
                  </VStack>
                )}
              </HStack>
            </FormControl>
            {userData?.userType === 'expert' && (
              <>
                <Text className="mt-4 text-sm text-typography-600">
                  Focus areas are set by the Primum team. To request changes, please{' '}
                  <LinkText
                    onPress={supportPrompt.open}
                    className="text-sm text-primary-500 hover:text-primary-600">
                    contact support
                  </LinkText>
                  .
                </Text>
                <SupportForm
                  isOpen={supportPrompt.isFormOpen}
                  onClose={supportPrompt.closeForm}
                  initialValues={{
                    reason: 'Profile Update',
                    name: `${userData?.firstName || ''} ${userData?.lastName || ''}`.trim(),
                  }}
                  firstName={userData?.firstName || undefined}
                  lastName={userData?.lastName || undefined}
                  institution={userData?.institution || undefined}
                  state={userData?.state || undefined}
                />
              </>
            )}
            <FormControl className="mt-2">
              <FormControlLabel className="mb-1">
                <FormControlLabelText size="sm">Subspecialties</FormControlLabelText>
              </FormControlLabel>
              <Controller
                name="subspecialtyIds"
                control={form.control}
                render={({ field }) => (
                  <SubspecialtyMultiSelect
                    value={field.value || []}
                    options={subspecialties}
                    onChange={field.onChange}
                  />
                )}
              />
            </FormControl>
            <FormControl
              className={twJoin('relative z-0', userData?.userType !== 'expert' ? 'mt-4' : 'mt-2')}>
              <FormControlLabel className="mb-1">
                <FormControlLabelText size="sm">Title</FormControlLabelText>
              </FormControlLabel>
              <Input size="sm">
                <Controller
                  control={form.control}
                  name="position"
                  render={({ field }) => (
                    <InputField
                      placeholder="Professional Title"
                      value={field.value}
                      onChangeText={field.onChange}
                      returnKeyType="next"
                    />
                  )}
                />
              </Input>
            </FormControl>
            <FormControl className="relative z-0 mt-2">
              <FormControlLabel className="mb-1">
                <FormControlLabelText size="sm">Institution</FormControlLabelText>
              </FormControlLabel>
              <Input size="sm">
                <Controller
                  control={form.control}
                  name="institution"
                  render={({ field }) => (
                    <InputField
                      placeholder="Institution"
                      value={field.value}
                      onChangeText={field.onChange}
                      returnKeyType="next"
                    />
                  )}
                />
              </Input>
            </FormControl>
            <HStack space="md" className="relative z-0 mt-2">
              <FormControl className="flex-1">
                <FormControlLabel className="mb-1">
                  <FormControlLabelText size="sm">City</FormControlLabelText>
                </FormControlLabel>
                <Input size="sm">
                  <Controller
                    control={form.control}
                    name="city"
                    render={({ field }) => (
                      <InputField
                        placeholder="City"
                        value={field.value}
                        onChangeText={field.onChange}
                        returnKeyType="done"
                      />
                    )}
                  />
                </Input>
              </FormControl>
              <FormControl className="flex-1" isInvalid={!!form.formState.errors.state}>
                <FormControlLabel className="mb-1">
                  <FormControlLabelText size="sm">State of Practice</FormControlLabelText>
                </FormControlLabel>
                <Select
                  selectedValue={form.watch('state') || undefined}
                  onValueChange={(value) => form.setValue('state', value, { shouldDirty: true })}>
                  <SelectTrigger className="h-9 justify-between">
                    <SelectInput placeholder="Select option" />
                    <SelectIcon className="mr-3" as={ChevronDownIcon} />
                  </SelectTrigger>
                  <SelectPortal>
                    <SelectBackdrop />
                    <SelectContent className="max-h-[70%]">
                      <SelectDragIndicatorWrapper>
                        <SelectDragIndicator />
                      </SelectDragIndicatorWrapper>
                      <SelectScrollView>
                        {US_STATES.map((state) => (
                          <SelectItem key={state.abbr} label={state.name} value={state.abbr} />
                        ))}
                      </SelectScrollView>
                    </SelectContent>
                  </SelectPortal>
                </Select>
                <FormControlError className="mt-1">
                  <FormControlErrorIcon size="xs" as={AlertCircleIcon} />
                  <FormControlErrorText size="xs">
                    {form.formState.errors.state?.message as string}
                  </FormControlErrorText>
                </FormControlError>
              </FormControl>
            </HStack>
          </VStack>
        </KeyboardAwareScrollView>
      </VStack>
      <Modal
        isOpen={showRemoveAvatarModal}
        onClose={() => setShowRemoveAvatarModal(false)}
        size="md">
        <ModalBackdrop />
        <ModalContent>
          <ModalHeader>
            <Heading size="h2-xl">Remove photo</Heading>
            <ModalCloseButton onPress={() => setShowRemoveAvatarModal(false)}>
              <Icon as={XIcon} />
            </ModalCloseButton>
          </ModalHeader>
          <ModalBody>
            <Text>Are you sure you want to remove your profile photo?</Text>
          </ModalBody>
          <ModalFooter className="w-full flex-col justify-start gap-4">
            <Button
              disabled={isRemovingAvatar}
              className={twJoin('w-full', isRemovingAvatar && 'opacity-50')}
              onPress={handleRemoveAvatar}>
              {isRemovingAvatar ? <Spinner size="small" /> : <ButtonText>Remove photo</ButtonText>}
            </Button>
            <Button
              className="w-full"
              variant="outline"
              action="secondary"
              onPress={() => setShowRemoveAvatarModal(false)}>
              <ButtonText>Cancel</ButtonText>
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </>
  );
}

export default EditProfessionalScreen;
